/* ============================================================
   VERDANO Moos-Logo Konfigurator
   Custom Element: <verdano-configurator>
   ============================================================ */

/* ---- Three.js loader (lazy, only when STL is needed) ---- */
/* Uses esm.sh which pre-bundles dependencies so "three" imports resolve correctly */
let threeModules = null;
async function loadThreeJS() {
  if (threeModules) return threeModules;

  // esm.sh bundles all dependencies inline, resolving "three" imports automatically
  const THREE = await import('https://esm.sh/three@0.160.0');
  const { SVGLoader } = await import('https://esm.sh/three@0.160.0/examples/jsm/loaders/SVGLoader.js');
  const { STLExporter } = await import('https://esm.sh/three@0.160.0/examples/jsm/exporters/STLExporter.js');

  threeModules = { THREE, SVGLoader, STLExporter };
  return threeModules;
}

/* ---- Generate solid STL from filled SVG shapes ---- */
async function svgToSolidSTL(svgString, targetHeightCm, depthMm) {
  const { THREE, SVGLoader, STLExporter } = await loadThreeJS();

  const loader = new SVGLoader();
  const svgData = loader.parse(svgString);

  const shapes = [];
  for (const path of svgData.paths) {
    const pathShapes = SVGLoader.createShapes(path);
    for (const shape of pathShapes) shapes.push(shape);
  }

  if (shapes.length === 0) {
    throw new Error('Keine Shapes im SVG gefunden');
  }

  const group = new THREE.Group();
  const extrudeSettings = { depth: depthMm, bevelEnabled: false, steps: 1, curveSegments: 24 };

  for (const shape of shapes) {
    const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    group.add(new THREE.Mesh(geo));
  }

  // Scale to target height in mm
  const box = new THREE.Box3().setFromObject(group);
  const size = new THREE.Vector3();
  box.getSize(size);
  const scale = (targetHeightCm * 10) / size.y;
  const center = new THREE.Vector3();
  box.getCenter(center);

  group.children.forEach(mesh => {
    mesh.geometry.translate(-center.x, -center.y, 0);
    mesh.geometry.applyMatrix4(new THREE.Matrix4().makeScale(scale, scale, 1));
  });

  group.rotation.x = Math.PI;  // SVG Y-down → STL Y-up
  group.updateMatrixWorld(true);

  const exporter = new STLExporter();
  return exporter.parse(group, { binary: false });
}

/* ---- Generate outline (wall-only) STL from stroked SVG paths ---- */
/* Builds 3D ribbons along each sub-path using the stroke width as wall thickness */
async function svgToOutlineSTL(outlineSvgString, targetHeightCm, depthMm) {
  const { THREE, SVGLoader, STLExporter } = await loadThreeJS();

  const loader = new SVGLoader();
  const svgData = loader.parse(outlineSvgString);

  // Compute SVG bounding box to derive scale
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const path of svgData.paths) {
    for (const subPath of path.subPaths) {
      for (const p of subPath.getPoints()) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
    }
  }

  const svgHeightUnits = maxY - minY;
  if (svgHeightUnits <= 0) throw new Error('Ungueltiger SVG viewBox');

  const targetHeightMm = targetHeightCm * 10;
  const scale = targetHeightMm / svgHeightUnits;

  // Wall thickness in mm (converted from SVG stroke-width, with sensible minimum)
  const group = new THREE.Group();

  for (const path of svgData.paths) {
    // Wall thickness in mm — fixed at 3mm for good 3D-print stability
    const strokeWidthMm = 3;

    for (const subPath of path.subPaths) {
      const points = subPath.getPoints();
      if (points.length < 2) continue;

      const geom = buildRibbonGeometry(THREE, points, strokeWidthMm, depthMm, scale);
      if (geom) group.add(new THREE.Mesh(geom));
    }
  }

  // Center the group
  const bbox = new THREE.Box3().setFromObject(group);
  const center = new THREE.Vector3();
  bbox.getCenter(center);
  group.children.forEach(mesh => {
    mesh.geometry.translate(-center.x, -center.y, 0);
  });

  group.rotation.x = Math.PI;
  group.updateMatrixWorld(true);

  const exporter = new STLExporter();
  return exporter.parse(group, { binary: false });
}

/* Build a 3D "ribbon" (extruded band) along a 2D polyline.
   Points are in SVG units. Output is in mm (scaled). */
function buildRibbonGeometry(THREE, points, widthMm, depthMm, scale) {
  const n = points.length;
  if (n < 2) return null;

  const isClosed = Math.hypot(points[0].x - points[n-1].x, points[0].y - points[n-1].y) < 0.01;
  const halfWidthMm = widthMm / 2;

  // Compute averaged normals at each point (in normalized 2D)
  const normals = [];
  for (let i = 0; i < n; i++) {
    let prev, next;
    if (isClosed) {
      prev = points[(i - 1 + n) % n];
      next = points[(i + 1) % n];
    } else {
      prev = i > 0 ? points[i - 1] : points[i];
      next = i < n - 1 ? points[i + 1] : points[i];
    }
    const tx = next.x - prev.x;
    const ty = next.y - prev.y;
    const len = Math.hypot(tx, ty) || 1;
    // Perpendicular (right-hand side of tangent direction)
    normals.push({ x: -ty / len, y: tx / len });
  }

  // Build vertices in MM space: 4 per point (inner/outer × bottom/top)
  const vertices = [];
  for (let i = 0; i < n; i++) {
    const p = points[i];
    const nm = normals[i];

    // Point in mm (scale SVG units to mm)
    const px = p.x * scale;
    const py = p.y * scale;

    // Offset the point by ±halfWidth along the normal (in mm)
    const innerX = px - nm.x * halfWidthMm;
    const innerY = py - nm.y * halfWidthMm;
    const outerX = px + nm.x * halfWidthMm;
    const outerY = py + nm.y * halfWidthMm;

    vertices.push(innerX, innerY, 0);       // 0: inner bottom
    vertices.push(outerX, outerY, 0);       // 1: outer bottom
    vertices.push(innerX, innerY, depthMm); // 2: inner top
    vertices.push(outerX, outerY, depthMm); // 3: outer top
  }

  // Build indices for faces
  const indices = [];
  const segmentCount = isClosed ? n : n - 1;

  for (let s = 0; s < segmentCount; s++) {
    const i = s;
    const j = (s + 1) % n;

    const a = i * 4; // current point's 4 vertices
    const b = j * 4; // next point's 4 vertices

    // Outer wall (outer-bottom → outer-top between points)
    indices.push(a + 1, b + 1, b + 3);
    indices.push(a + 1, b + 3, a + 3);

    // Inner wall (reversed winding)
    indices.push(a, a + 2, b + 2);
    indices.push(a, b + 2, b);

    // Top cap (inner-top → outer-top)
    indices.push(a + 2, a + 3, b + 3);
    indices.push(a + 2, b + 3, b + 2);

    // Bottom cap (reversed)
    indices.push(a, b, b + 1);
    indices.push(a, b + 1, a + 1);
  }

  // If not closed, cap the ends
  if (!isClosed) {
    // Start cap (at point 0)
    indices.push(0, 2, 1);
    indices.push(1, 2, 3);

    // End cap (at point n-1)
    const e = (n - 1) * 4;
    indices.push(e, e + 1, e + 2);
    indices.push(e + 1, e + 3, e + 2);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

/* Backwards-compatible wrapper (for solid STL) */
async function svgToSTL(svgString, targetHeightCm, depthMm) {
  return svgToSolidSTL(svgString, targetHeightCm, depthMm);
}

class VerdanoConfigurator extends HTMLElement {
  constructor() {
    super();

    // State
    this.currentStep = 1;
    this.svgContent = null;
    this.svgViewBox = null; // { width, height }
    this.config = {
      height: 15, // cm
      moss: 'island-hell',
      frame: 'schwarz'
    };
    this.mossTextures = {};
    this.texturesLoaded = {};

    // Settings from data attributes
    this.basePrice = parseInt(this.dataset.basePrice || '0', 10);
    this.pricePerSqm = parseInt(this.dataset.pricePerSqm || '20000', 10);
    this.currency = this.dataset.currency || 'EUR';
    this.supabaseUrl = this.dataset.supabaseUrl || '';
    this.supabaseKey = this.dataset.supabaseKey || '';
    this.addonVariantId = this.dataset.addonVariantId || '';
    this.mainVariantId = this.dataset.mainVariantId || '';
    this.previewBgUrl = this.dataset.previewBg || '';
    this.previewBgImg = null;
    this.previewBgLoaded = false;
  }

  connectedCallback() {
    this.cacheElements();
    this.bindEvents();
    this.preloadTextures();
  }

  // ---- DOM References ----
  cacheElements() {
    this.fileInput = this.querySelector('#file-input');
    this.uploadZone = this.querySelector('#upload-zone');
    this.uploadSuccess = this.querySelector('#upload-success');
    this.uploadFilename = this.querySelector('#upload-filename');
    this.uploadChange = this.querySelector('#upload-change');
    this.previewEmpty = this.querySelector('#preview-empty');
    this.canvas = this.querySelector('#moss-preview-canvas');
    this.previewInfo = this.querySelector('#preview-info');
    this.step1 = this.querySelector('#step-1');
    this.step2 = this.querySelector('#step-2');
    this.btnSubmit = this.querySelector('#btn-add-to-cart');
    this.form = this.querySelector('form');

    // Info displays
    this.infoDimensions = this.querySelector('#info-dimensions');
    this.infoArea = this.querySelector('#info-area');
    this.infoPrice = this.querySelector('#info-price');
    this.priceBase = this.querySelector('#price-base');
    this.priceSurcharge = this.querySelector('#price-surcharge');
    this.priceTotal = this.querySelector('#price-total');
    this.priceAreaLabel = this.querySelector('#price-area-label');

    // Measurement overlay
    this.measureOverlay = this.querySelector('#measure-overlay');
    this.measureW = this.querySelector('#measure-w');
    this.measureH = this.querySelector('#measure-h');
    this.measureWLabel = this.querySelector('#measure-w-label');
    this.measureHLabel = this.querySelector('#measure-h-label');

    // Hidden inputs
    this.propSvgUrl = this.querySelector('#prop-svg-url');
    this.propSize = this.querySelector('#prop-size');
    this.propFrame = this.querySelector('#prop-frame');
    this.propMoss = this.querySelector('#prop-moss');
    this.propDimensions = this.querySelector('#prop-dimensions');
    this.propArea = this.querySelector('#prop-area');
    this.propSurcharge = this.querySelector('#prop-surcharge');
    this.propTotalPrice = this.querySelector('#prop-total-price');
  }

  // ---- Event Bindings ----
  bindEvents() {
    // File upload
    this.fileInput.addEventListener('change', (e) => this.handleFileUpload(e.target.files[0]));
    this.uploadChange.addEventListener('click', () => this.resetUpload());

    // Drag & drop
    this.uploadZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.uploadZone.classList.add('dragover');
    });
    this.uploadZone.addEventListener('dragleave', () => {
      this.uploadZone.classList.remove('dragover');
    });
    this.uploadZone.addEventListener('drop', (e) => {
      e.preventDefault();
      this.uploadZone.classList.remove('dragover');
      if (e.dataTransfer.files.length) {
        this.handleFileUpload(e.dataTransfer.files[0]);
      }
    });

    // Option cards
    this.querySelectorAll('.configurator__card').forEach(card => {
      card.addEventListener('click', () => this.handleCardClick(card));
    });

    // Form submit
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  // ---- Texture Preloading ----
  preloadTextures() {
    const textureMap = {
      'island-hell': this.dataset.textureLight,
      'island-dunkel': this.dataset.textureDark,
      'kugelmoos': this.dataset.textureBall
    };
    for (const [key, url] of Object.entries(textureMap)) {
      if (url) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { this.texturesLoaded[key] = true; };
        img.src = url;
        this.mossTextures[key] = img;
      }
    }
    // Preload background image
    if (this.previewBgUrl) {
      const bgImg = new Image();
      bgImg.crossOrigin = 'anonymous';
      bgImg.onload = () => {
        this.previewBgLoaded = true;
        // If SVG is already uploaded, re-render with background
        if (this.svgContent) this.renderPreview();
      };
      bgImg.src = this.previewBgUrl;
      this.previewBgImg = bgImg;
    }
  }

  // ============================================================
  // STEP 1: FILE UPLOAD
  // ============================================================

  async handleFileUpload(file) {
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'svg') {
      alert('Bitte eine SVG-Datei hochladen.');
      return;
    }

    try {
      const text = await file.text();
      this.processSVG(text, file.name);
    } catch (err) {
      console.error('Fehler beim Lesen der Datei:', err);
      alert('Die Datei konnte nicht gelesen werden.');
    }
  }

  processSVG(svgString, filename) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgEl = doc.querySelector('svg');

    if (!svgEl) {
      alert('Keine gueltige SVG-Datei.');
      return;
    }

    // Extract viewBox dimensions
    let vb = svgEl.getAttribute('viewBox');
    if (vb) {
      const parts = vb.split(/[\s,]+/).map(Number);
      this.svgViewBox = { width: parts[2], height: parts[3] };
    } else {
      const w = parseFloat(svgEl.getAttribute('width')) || 100;
      const h = parseFloat(svgEl.getAttribute('height')) || 100;
      svgEl.setAttribute('viewBox', `0 0 ${w} ${h}`);
      this.svgViewBox = { width: w, height: h };
    }

    this.svgContent = svgString;
    this.svgElement = svgEl;
    this.originalFilename = filename;

    // Generate solid + outline
    this.solidSVG = this.createSolidVersion(svgEl);
    this.outlineSVG = this.createOutlineVersion(svgEl);

    // Show success
    this.uploadFilename.textContent = filename;
    this.uploadZone.style.display = 'none';
    this.uploadSuccess.style.display = 'flex';

    // Show preview
    this.renderPreview();

    // Go to step 2
    this.goToStep(2);
  }

  createSolidVersion(svgEl) {
    const clone = svgEl.cloneNode(true);
    const shapes = clone.querySelectorAll('path, polygon, polyline, circle, ellipse, rect, line, text');
    shapes.forEach(s => {
      const currentFill = s.getAttribute('fill');
      if (currentFill !== 'none' && currentFill !== 'transparent') {
        s.setAttribute('fill', '#000000');
      } else if (!currentFill) {
        s.setAttribute('fill', '#000000');
      }
      s.removeAttribute('stroke');
      s.removeAttribute('stroke-width');
      s.style.fill = '';
      s.style.stroke = '';
    });
    // Also handle groups with fill
    clone.querySelectorAll('g').forEach(g => {
      const gFill = g.getAttribute('fill');
      if (gFill && gFill !== 'none' && gFill !== 'transparent') {
        g.setAttribute('fill', '#000000');
      }
      g.style.fill = '';
    });
    return new XMLSerializer().serializeToString(clone);
  }

  createOutlineVersion(svgEl) {
    const clone = svgEl.cloneNode(true);
    const shapes = clone.querySelectorAll('path, polygon, polyline, circle, ellipse, rect, line, text');
    shapes.forEach(s => {
      s.setAttribute('fill', 'none');
      s.setAttribute('stroke', '#000000');
      s.setAttribute('stroke-width', '2');
      s.style.fill = '';
      s.style.stroke = '';
    });
    clone.querySelectorAll('g').forEach(g => {
      g.setAttribute('fill', 'none');
      g.style.fill = '';
    });
    return new XMLSerializer().serializeToString(clone);
  }

  resetUpload() {
    this.svgContent = null;
    this.svgViewBox = null;
    this.fileInput.value = '';
    this.uploadZone.style.display = '';
    this.uploadSuccess.style.display = 'none';
    this.previewEmpty.style.display = '';
    this.canvas.style.display = 'none';
    this.previewInfo.style.display = 'none';
    if (this.measureOverlay) this.measureOverlay.style.display = 'none';

    // Re-disable step 2 and reset example price
    this.step2.classList.add('configurator__step--disabled');
    this.priceAreaLabel.textContent = 'Beispiel';
    this.priceSurcharge.textContent = '150,00 \u20AC';
    this.priceTotal.textContent = '250,00 \u20AC';
    const totalLabel = this.querySelector('#price-total-label');
    if (totalLabel) totalLabel.textContent = 'Gesamtpreis (Beispiel)';
    this.btnSubmit.disabled = true;

    this.currentStep = 1;
  }

  // ============================================================
  // STEP 2: CONFIGURATION
  // ============================================================

  handleCardClick(card) {
    const option = card.dataset.option;
    const value = card.dataset.value;

    // Deselect siblings
    card.closest('.configurator__cards').querySelectorAll('.configurator__card').forEach(c => c.classList.remove('active'));
    card.classList.add('active');

    // Update config
    if (option === 'size') this.config.height = parseInt(value, 10);
    if (option === 'moss') this.config.moss = value;
    if (option === 'frame') this.config.frame = value;

    // Update preview + price
    this.renderPreview();
    this.updatePrice();
  }

  // ============================================================
  // PREVIEW RENDERING
  // ============================================================

  renderPreview() {
    if (!this.svgContent || !this.svgViewBox) return;

    this.previewEmpty.style.display = 'none';
    this.canvas.style.display = 'block';
    this.previewInfo.style.display = 'grid';
    if (this.measureOverlay) this.measureOverlay.style.display = 'block';

    const ctx = this.canvas.getContext('2d');
    const canvasSize = 800;
    this.canvas.width = canvasSize;
    this.canvas.height = canvasSize;

    const svgAspect = this.svgViewBox.width / this.svgViewBox.height;
    let drawW, drawH, offsetX, offsetY;

    // Size-dependent scaling: 25cm = full reference size, smaller sizes scale down proportionally
    // Max size (25cm): logo takes 55% of canvas height
    const maxScale = 0.55;
    const referenceHeightCm = 25;
    const sizeRatio = this.config.height / referenceHeightCm; // 0.6 for 15cm, 1.0 for 25cm
    const targetScale = maxScale * sizeRatio;

    if (svgAspect > 1) {
      drawW = canvasSize * targetScale;
      drawH = drawW / svgAspect;
    } else {
      drawH = canvasSize * targetScale;
      drawW = drawH * svgAspect;
    }
    offsetX = (canvasSize - drawW) / 2;
    offsetY = (canvasSize - drawH) / 2;

    // Clear canvas
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    // Step 0: Draw background scene
    if (this.previewBgImg && this.previewBgLoaded) {
      // Cover the canvas with the background image
      const bgAspect = this.previewBgImg.width / this.previewBgImg.height;
      let bgDrawW, bgDrawH, bgOffsetX, bgOffsetY;
      if (bgAspect > 1) {
        bgDrawH = canvasSize;
        bgDrawW = bgDrawH * bgAspect;
        bgOffsetX = (canvasSize - bgDrawW) / 2;
        bgOffsetY = 0;
      } else {
        bgDrawW = canvasSize;
        bgDrawH = bgDrawW / bgAspect;
        bgOffsetX = 0;
        bgOffsetY = (canvasSize - bgDrawH) / 2;
      }
      ctx.drawImage(this.previewBgImg, bgOffsetX, bgOffsetY, bgDrawW, bgDrawH);
    }

    // Prepare SVG image
    const svgBlob = new Blob([this.solidSVG], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const svgImg = new Image();

    svgImg.onload = () => {
      const frameColors = {
        'schwarz': '#1A1917',
        'weiss': '#F5F3EE',
        'holz': '#C4A265'
      };
      const frameColor = frameColors[this.config.frame] || '#1A1917';

      // ===== 3D DEPTH EFFECT =====
      // Light from top-right, shadow to bottom-left — subtle
      const depthScale = Math.max(0.7, sizeRatio);
      const depthLayers = 10;
      const depthOffset = 0.7 * depthScale;

      // ---- 1. Main cast shadow (soft, gentle, falls to bottom-LEFT) ----
      const shadowOffsetX = -10 * depthScale;
      const shadowOffsetY = 14 * depthScale;
      const shadowBlur = 16 * depthScale;

      const wallShadowCanvas = document.createElement('canvas');
      wallShadowCanvas.width = canvasSize;
      wallShadowCanvas.height = canvasSize;
      const wallShadowCtx = wallShadowCanvas.getContext('2d');
      wallShadowCtx.drawImage(svgImg, offsetX + shadowOffsetX, offsetY + shadowOffsetY, drawW, drawH);
      wallShadowCtx.globalCompositeOperation = 'source-in';
      wallShadowCtx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      wallShadowCtx.fillRect(0, 0, canvasSize, canvasSize);

      ctx.save();
      ctx.filter = `blur(${shadowBlur}px)`;
      ctx.globalAlpha = 0.5;
      ctx.drawImage(wallShadowCanvas, 0, 0);
      ctx.restore();

      // ---- 1b. Tight contact shadow at the logo edge (makes it "pop") ----
      const aoCanvas = document.createElement('canvas');
      aoCanvas.width = canvasSize;
      aoCanvas.height = canvasSize;
      const aoCtx = aoCanvas.getContext('2d');
      aoCtx.drawImage(svgImg, offsetX - 1, offsetY + 3, drawW, drawH);
      aoCtx.globalCompositeOperation = 'source-in';
      aoCtx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      aoCtx.fillRect(0, 0, canvasSize, canvasSize);

      ctx.save();
      ctx.filter = 'blur(3px)';
      ctx.globalAlpha = 0.55;
      ctx.drawImage(aoCanvas, 0, 0);
      ctx.restore();

      // ---- 2. Side depth (stacked frame-colored layers going LEFT-DOWN) ----
      const depthCanvas = document.createElement('canvas');
      depthCanvas.width = canvasSize;
      depthCanvas.height = canvasSize;
      const depthCtx = depthCanvas.getContext('2d');

      for (let i = depthLayers; i > 0; i--) {
        depthCtx.drawImage(
          svgImg,
          offsetX - (i * depthOffset),
          offsetY + (i * depthOffset),
          drawW,
          drawH
        );
      }
      depthCtx.globalCompositeOperation = 'source-in';

      // Gradient: lighter top-right (light side), darker bottom-left (shadow side)
      const depthGradient = depthCtx.createLinearGradient(
        offsetX + drawW, offsetY,
        offsetX - depthLayers * depthOffset,
        offsetY + drawH + depthLayers * depthOffset
      );
      depthGradient.addColorStop(0, this.darkenColor(frameColor, 0.12));
      depthGradient.addColorStop(1, this.darkenColor(frameColor, 0.4));
      depthCtx.fillStyle = depthGradient;
      depthCtx.fillRect(0, 0, canvasSize, canvasSize);

      ctx.save();
      ctx.drawImage(depthCanvas, 0, 0);
      ctx.restore();

      // ---- 3. Frame outline (front edge of the frame) ----
      const strokeWidth = Math.max(3, canvasSize * 0.004);
      const outlineCanvas = document.createElement('canvas');
      outlineCanvas.width = canvasSize;
      outlineCanvas.height = canvasSize;
      const outlineCtx = outlineCanvas.getContext('2d');

      // Draw enlarged shape in all directions
      for (let dx = -strokeWidth; dx <= strokeWidth; dx += 1) {
        for (let dy = -strokeWidth; dy <= strokeWidth; dy += 1) {
          if (dx * dx + dy * dy <= strokeWidth * strokeWidth) {
            outlineCtx.drawImage(svgImg, offsetX + dx, offsetY + dy, drawW, drawH);
          }
        }
      }
      outlineCtx.globalCompositeOperation = 'source-in';
      outlineCtx.fillStyle = frameColor;
      outlineCtx.fillRect(0, 0, canvasSize, canvasSize);

      ctx.save();
      ctx.drawImage(outlineCanvas, 0, 0);
      ctx.restore();

      // ---- 4. Moss fill (on top of frame) ----
      const mossCanvas = document.createElement('canvas');
      mossCanvas.width = canvasSize;
      mossCanvas.height = canvasSize;
      const mossCtx = mossCanvas.getContext('2d');

      // Draw shape as mask
      mossCtx.drawImage(svgImg, offsetX, offsetY, drawW, drawH);

      // Fill with moss texture
      mossCtx.globalCompositeOperation = 'source-in';
      const textureImg = this.mossTextures[this.config.moss];
      if (textureImg && this.texturesLoaded[this.config.moss]) {
        const pat = mossCtx.createPattern(textureImg, 'repeat');
        mossCtx.fillStyle = pat;
        mossCtx.fillRect(0, 0, canvasSize, canvasSize);
      } else {
        const colors = {
          'island-hell': '#8FAE85',
          'island-dunkel': '#2D5A3D',
          'kugelmoos': '#4A6B3A'
        };
        mossCtx.fillStyle = colors[this.config.moss] || '#8FAE85';
        mossCtx.fillRect(0, 0, canvasSize, canvasSize);
      }

      // Add inner highlight: light from top-right, shadow at bottom-left
      mossCtx.globalCompositeOperation = 'source-atop';
      const gradient = mossCtx.createLinearGradient(
        offsetX + drawW, offsetY,          // top-right = light source
        offsetX, offsetY + drawH           // bottom-left = shadow
      );
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.22)');
      gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.04)');
      gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.08)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0.2)');
      mossCtx.fillStyle = gradient;
      mossCtx.fillRect(offsetX, offsetY, drawW, drawH);

      ctx.save();
      ctx.drawImage(mossCanvas, 0, 0);
      ctx.restore();

      URL.revokeObjectURL(svgUrl);
      this.updatePrice();
      this.updateMeasurements(offsetX, offsetY, drawW, drawH, canvasSize);
    };

    svgImg.onerror = () => {
      console.error('SVG konnte nicht als Bild geladen werden');
      URL.revokeObjectURL(svgUrl);
    };

    svgImg.src = svgUrl;
  }

  // Position the dimension measurement lines (in percent relative to canvas)
  updateMeasurements(offsetX, offsetY, drawW, drawH, canvasSize) {
    if (!this.measureOverlay || !this.svgViewBox) return;

    const dims = this.calculateDimensions();
    if (!dims) return;

    // Logo bounds in percent of canvas
    const logoLeftPct = (offsetX / canvasSize) * 100;
    const logoRightPct = ((offsetX + drawW) / canvasSize) * 100;
    const logoTopPct = (offsetY / canvasSize) * 100;
    const logoBottomPct = ((offsetY + drawH) / canvasSize) * 100;

    // Width line: 24px below logo, spanning logo width
    const wLinePadding = 3; // percent gap below logo
    this.measureW.style.left = logoLeftPct + '%';
    this.measureW.style.right = (100 - logoRightPct) + '%';
    this.measureW.style.top = (logoBottomPct + wLinePadding) + '%';
    this.measureW.style.height = '14px';
    this.measureWLabel.textContent = `${dims.widthCm} cm`;

    // Height line: 24px right of logo, spanning logo height
    const hLinePadding = 3;
    this.measureH.style.top = logoTopPct + '%';
    this.measureH.style.bottom = (100 - logoBottomPct) + '%';
    this.measureH.style.left = (logoRightPct + hLinePadding) + '%';
    this.measureH.style.width = '14px';
    this.measureHLabel.textContent = `${dims.heightCm} cm`;
  }

  // Helper: darken hex color
  darkenColor(hex, factor) {
    const c = hex.replace('#', '');
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    const nr = Math.round(r * (1 - factor));
    const ng = Math.round(g * (1 - factor));
    const nb = Math.round(b * (1 - factor));
    return `rgb(${nr}, ${ng}, ${nb})`;
  }

  // ============================================================
  // PRICE CALCULATION
  // ============================================================

  calculateDimensions() {
    if (!this.svgViewBox) return null;
    const aspect = this.svgViewBox.width / this.svgViewBox.height;
    const heightCm = this.config.height;
    const widthCm = Math.round(heightCm * aspect * 10) / 10;
    const areaCm2 = heightCm * widthCm;
    const areaM2 = areaCm2 / 10000;
    return { heightCm, widthCm, areaCm2, areaM2 };
  }

  updatePrice() {
    const dims = this.calculateDimensions();
    if (!dims) return;

    // Surcharge rounded UP to full Euros (matches cart calculation)
    const surchargeRawCents = Math.round(dims.areaM2 * this.pricePerSqm);
    const surchargeEur = Math.ceil(surchargeRawCents / 100);
    const surchargeRaw = surchargeEur * 100;  // back to cents
    const totalRaw = this.basePrice + surchargeRaw;

    // Format money
    const fmt = (cents) => {
      const eur = (cents / 100).toFixed(2).replace('.', ',');
      return `${eur} \u20AC`;
    };

    // Format area in m²
    const areaM2Display = dims.areaM2 < 0.01
      ? dims.areaM2.toFixed(4).replace('.', ',')
      : dims.areaM2.toFixed(2).replace('.', ',');

    // Update UI
    this.infoDimensions.textContent = `${dims.heightCm} \u00D7 ${dims.widthCm} cm`;
    this.infoArea.textContent = `${areaM2Display} m\u00B2`;
    this.infoPrice.textContent = fmt(totalRaw);
    this.priceAreaLabel.textContent = `${areaM2Display} m\u00B2`;
    this.priceSurcharge.textContent = fmt(surchargeRaw);
    this.priceTotal.textContent = fmt(totalRaw);
    const totalLabelEl = this.querySelector('#price-total-label');
    if (totalLabelEl) totalLabelEl.textContent = 'Gesamtpreis';

    // Update hidden inputs
    const mossLabels = {
      'island-hell': 'Island-Moos Hellgruen',
      'island-dunkel': 'Island-Moos Dunkelgruen',
      'kugelmoos': 'Kugelmoos'
    };
    const frameLabels = {
      'schwarz': 'Schwarz',
      'weiss': 'Weiss',
      'holz': 'Holz'
    };

    this.propSize.value = `${dims.heightCm} cm`;
    this.propFrame.value = frameLabels[this.config.frame] || this.config.frame;
    this.propMoss.value = mossLabels[this.config.moss] || this.config.moss;
    this.propDimensions.value = `${dims.heightCm} x ${dims.widthCm} cm`;
    this.propArea.value = `${areaM2Display} m\u00B2`;
    this.propSurcharge.value = fmt(surchargeRaw);
    this.propTotalPrice.value = fmt(totalRaw);

    // Enable submit
    this.btnSubmit.disabled = false;
  }

  // ============================================================
  // STEP NAVIGATION
  // ============================================================

  goToStep(step) {
    this.currentStep = step;
    if (step === 2) {
      this.step2.classList.remove('configurator__step--disabled');
      // Smooth scroll to step 2 on mobile
      if (window.innerWidth < 768) {
        this.step2.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  // ============================================================
  // SUPABASE UPLOAD
  // ============================================================

  uploadToSupabase(content, folderPath, filename, contentType = 'image/svg+xml', onProgress = null) {
    return new Promise((resolve) => {
      if (!this.supabaseUrl || !this.supabaseKey) {
        console.warn('Supabase nicht konfiguriert — ueberspringe Upload');
        resolve(false);
        return;
      }

      const safeName = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `orders/${folderPath}/${safeName}`;

      // Wrap content in Blob so we get a known byte size for progress calculation
      let body = content;
      if (typeof content === 'string') {
        body = new Blob([content], {
          type: filename.toLowerCase().endsWith('.stl') ? 'application/octet-stream' : contentType
        });
      }
      const totalBytes = body.size || 0;

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.supabaseUrl}/storage/v1/object/logo-uploads/${path}`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${this.supabaseKey}`);
      xhr.setRequestHeader('apikey', this.supabaseKey);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.setRequestHeader('x-upsert', 'true');

      // Live upload-progress via xhr.upload
      xhr.upload.addEventListener('progress', (evt) => {
        if (onProgress && evt.lengthComputable) {
          onProgress(evt.loaded, totalBytes || evt.total);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (onProgress) onProgress(totalBytes, totalBytes); // mark 100%
          console.log(`[Verdano] Upload OK: ${filename}`);
          resolve(true);
        } else {
          console.error(`[Verdano] Upload fehlgeschlagen (${filename}):`, xhr.status, xhr.responseText);
          resolve(false);
        }
      });

      xhr.addEventListener('error', () => {
        console.error(`[Verdano] Upload-Exception (${filename})`);
        resolve(false);
      });

      xhr.addEventListener('abort', () => resolve(false));

      xhr.send(body);
    });
  }

  getZipDownloadUrl(orderId) {
    return `${this.supabaseUrl}/functions/v1/download-logos?id=${orderId}`;
  }

  // ============================================================
  // FORM SUBMIT
  // ============================================================

  async handleSubmit(e) {
    e.preventDefault();

    if (!this.svgContent) {
      alert('Bitte lade zuerst ein Logo hoch.');
      return;
    }

    // Show progress UI
    const textEl = this.btnSubmit.querySelector('.configurator__submit-text');
    const progressEl = this.btnSubmit.querySelector('#submit-progress');
    const progressLabel = this.querySelector('#progress-label');
    const progressRing = this.querySelector('#progress-ring-fg');
    const RING_CIRCUMFERENCE = 94.25; // 2 * PI * 15

    const setProgress = (percent, label) => {
      if (progressLabel) progressLabel.textContent = label;
      if (progressRing) {
        const offset = RING_CIRCUMFERENCE - (percent / 100) * RING_CIRCUMFERENCE;
        progressRing.style.strokeDashoffset = offset;
      }
    };

    textEl.style.display = 'none';
    progressEl.style.display = 'inline-flex';
    this.btnSubmit.disabled = true;
    setProgress(0, 'Druckdatei wird erstellt...');

    try {
      // Generate unique order folder ID
      const orderId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const baseName = this.originalFilename.replace(/\.[^.]+$/, '');

      // Calculate dimensions (needed for STL scaling)
      const dims = this.calculateDimensions();
      const surchargeRaw = Math.round(dims.areaM2 * this.pricePerSqm);
      const surchargeEur = Math.ceil(surchargeRaw / 100);

      // Generate STL files from solidSVG
      // - Solid: extruded to 1.5mm depth at target height
      // - Outline (frame): same XY dimensions, but 15mm depth
      // ====== STEP 1: Generate STL files (0 → 33%) ======
      setProgress(5, 'Druckdatei wird erstellt...');
      let solidSTL = null;
      let outlineSTL = null;
      try {
        console.log('[Verdano] STL-Generierung gestartet...');
        // Generate sequentially to show intermediate progress
        solidSTL = await svgToSolidSTL(this.solidSVG, dims.heightCm, 1.5);
        setProgress(20, 'Druckdatei wird erstellt...');
        outlineSTL = await svgToOutlineSTL(this.outlineSVG, dims.heightCm, 15);
        setProgress(33, 'Druckdatei wird erstellt...');
        console.log('[Verdano] STL generiert:', {
          solidLen: solidSTL?.length,
          outlineLen: outlineSTL?.length
        });
      } catch (stlErr) {
        console.error('[Verdano] STL-Generierung Fehler:', stlErr);
      }

      // ====== STEP 2: Upload files (33 → 80%) with live byte-progress ======
      setProgress(35, 'Dateien werden hochgeladen...');
      const fileJobs = [
        { content: this.svgContent, name: `${baseName}_original.svg`, type: 'image/svg+xml' },
        { content: this.solidSVG, name: `${baseName}_solid.svg`, type: 'image/svg+xml' },
        { content: this.outlineSVG, name: `${baseName}_outline.svg`, type: 'image/svg+xml' }
      ];
      if (solidSTL) {
        fileJobs.push({ content: solidSTL, name: `${baseName}_solid_${dims.heightCm}cm_1.5mm.stl`, type: 'application/octet-stream' });
      }
      if (outlineSTL) {
        fileJobs.push({ content: outlineSTL, name: `${baseName}_outline_${dims.heightCm}cm_15mm.stl`, type: 'application/octet-stream' });
      }

      // Compute total bytes (estimate sizes) for aggregate progress
      const jobSizes = fileJobs.map(j => {
        if (typeof j.content === 'string') return new Blob([j.content]).size;
        return j.content.size || 0;
      });
      const totalBytes = jobSizes.reduce((a, b) => a + b, 0);

      // Track loaded bytes per job
      const loadedBytes = new Array(fileJobs.length).fill(0);
      let finishedCount = 0;

      // Smoothing: interpolate the ring toward the target to keep motion even
      // during bursty network events
      let displayedPct = 35;
      let targetPct = 35;
      let smoothTimer = setInterval(() => {
        if (Math.abs(displayedPct - targetPct) > 0.1) {
          displayedPct += (targetPct - displayedPct) * 0.18;
          setProgress(displayedPct, `Dateien werden hochgeladen... (${finishedCount}/${fileJobs.length})`);
        }
      }, 80);

      const updateTargetProgress = () => {
        const totalLoaded = loadedBytes.reduce((a, b) => a + b, 0);
        const uploadFraction = totalBytes > 0 ? totalLoaded / totalBytes : 0;
        targetPct = 35 + uploadFraction * 45; // maps 0-100% upload to 35-80% of total
      };

      const results = await Promise.all(fileJobs.map(async (job, idx) => {
        const ok = await this.uploadToSupabase(
          job.content,
          orderId,
          job.name,
          job.type,
          (loaded, total) => {
            loadedBytes[idx] = Math.min(loaded, jobSizes[idx] || total);
            updateTargetProgress();
          }
        );
        finishedCount++;
        loadedBytes[idx] = jobSizes[idx];  // ensure it's at 100% when done
        updateTargetProgress();
        return ok;
      }));

      clearInterval(smoothTimer);
      console.log('[Verdano] Upload results:', results);

      if (!results.some(ok => ok)) {
        throw new Error('Alle Uploads fehlgeschlagen');
      }

      setProgress(82, 'Wird zum Warenkorb hinzugefuegt...');

      // Set ZIP download URL
      const zipUrl = this.getZipDownloadUrl(orderId);

      // Build cart items array
      const mossLabels = {
        'island-hell': 'Island-Moos Hellgruen',
        'island-dunkel': 'Island-Moos Dunkelgruen',
        'kugelmoos': 'Kugelmoos'
      };
      const frameLabels = {
        'schwarz': 'Schwarz',
        'weiss': 'Weiss',
        'holz': 'Holz'
      };

      const mainItem = {
        id: parseInt(this.mainVariantId, 10),
        quantity: 1,
        properties: {
          'Logo-Dateien (ZIP)': zipUrl,
          'Groesse': `${dims.heightCm} cm`,
          'Masse': `${dims.heightCm} x ${dims.widthCm} cm`,
          'Flaeche': this.propArea.value,
          'Moosart': mossLabels[this.config.moss] || this.config.moss,
          'Rahmenfarbe': frameLabels[this.config.frame] || this.config.frame
        }
      };

      const cartDrawerEl = document.querySelector('cart-drawer');
      const cartDrawerSectionId = cartDrawerEl?.getAttribute('section-id') || 'cart-drawer';
      const sectionsToFetch = [cartDrawerSectionId, 'cart-json'];
      const hasAddon = this.addonVariantId && surchargeEur > 0;

      // Step 1: Add main product FIRST (without sections — we fetch those on the last request)
      const mainResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [mainItem], sections: hasAddon ? [] : sectionsToFetch })
      });

      if (!mainResponse.ok) {
        throw new Error('Warenkorb-Fehler (Hauptprodukt)');
      }

      setProgress(hasAddon ? 90 : 98, 'Wird zum Warenkorb hinzugefuegt...');

      let responseJson;

      if (hasAddon) {
        // Step 2: Add addon SECOND (with sections so we get updated drawer HTML)
        const addonItem = {
          id: parseInt(this.addonVariantId, 10),
          quantity: surchargeEur,
          properties: {
            '_Zugehoerig zu': `Moos-Logo (${dims.heightCm} x ${dims.widthCm} cm)`
          }
        };
        const addonResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [addonItem], sections: sectionsToFetch })
        });
        if (!addonResponse.ok) {
          throw new Error('Warenkorb-Fehler (Zuschlag)');
        }
        responseJson = await addonResponse.json();
      } else {
        responseJson = await mainResponse.json();
      }

      setProgress(100, 'Fertig!');

      // Build cartData exactly like the theme does (global.js line 3405)
      let cartData = {};
      try {
        const cartJsonSection = responseJson.sections?.['cart-json'] || '';
        const cartJsonPart = cartJsonSection.split('[split_94]')[1] || '{}';
        cartData = Object.assign({}, responseJson, JSON.parse(cartJsonPart));
      } catch (e) {
        cartData = responseJson;
      }

      // Dispatch event on document — same as theme's ProductForm
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            source: 'product-form',
            actionAfterATC: 'open_cart_drawer',
            variantId: this.mainVariantId,
            cartData,
            formData: { quantity: 1, id: this.mainVariantId }
          }
        })
      );

    } catch (err) {
      console.error('Fehler beim Hinzufuegen:', err);
      alert('Fehler beim Hinzufuegen zum Warenkorb. Bitte versuche es erneut.');
    } finally {
      // Small delay so the user sees "Fertig!" briefly before reset
      setTimeout(() => {
        textEl.style.display = '';
        progressEl.style.display = 'none';
        setProgress(0, 'Druckdatei wird erstellt...');
        this.btnSubmit.disabled = false;
      }, 400);
    }
  }
}

// Register custom element
customElements.define('verdano-configurator', VerdanoConfigurator);
